import * as React from "react";

import { RouteStore } from "bernie-core";
import { ClientWizardStateStore } from "src/stores/wizard/ClientWizardStateStore";

import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { FlightTypeMenu } from "../../WizardFlightPWA/components/FlightTypeMenu";
import { UitkLayoutConditionalGridSpan } from "uitk-react-layout-grid";
import { Travelers } from "src/components/shared/Travelers/Travelers";
import { ClassCodes, PreferredClassInput } from "src/components/shared/PreferredClassInput/PreferredClassInput";
import { NoRehydratationValues, TravelersTriggerTypes } from "src/components/shared/TravelersField/typings";
import { Experiment, ExperimentControl, ExperimentVariant } from "@shared-ui/experiment-context";
import { FlightType, PackagesSubLobs } from "src/stores/wizard/state/typings";
import {
  PreferredAirline,
  PreferredAirlineInput,
} from "src/components/shared/PreferredAirlineInput/PreferredAirlineInput";
import { CarTypeDropdown } from "../../WizardCarPWA/components/vendorOptions/CarTypeDropdown";

export type MenuFieldsProps = {
  currentSubLOB: string;
  routeStore: RouteStore;
  wizardState: ClientWizardStateStore;
  noRehydratationValues?: NoRehydratationValues;
  displayNewPackageFlightType: boolean;
  onFlightTypeUpdate: (FlightType: string) => void;
  showOneway: boolean;
  showMulticity: boolean;
  onSelectPreferredClassCode: (classCode: ClassCodes) => void;
  onSelectPreferredAirline: (flightAirline: PreferredAirline) => void;
  activeFlightType: FlightType;
};

export const MenuFields: React.FC<MenuFieldsProps> = ({
  currentSubLOB,
  routeStore,
  wizardState,
  noRehydratationValues,
  displayNewPackageFlightType,
  onFlightTypeUpdate,
  showOneway,
  showMulticity,
  onSelectPreferredClassCode,
  onSelectPreferredAirline,
  activeFlightType,
}) => {
  const { packageWizardState, globalWizardState, carWizardState } = wizardState;
  const { config: packageConfig } = packageWizardState;
  const { config: globalConfig } = globalWizardState;
  const { config: carConfig } = carWizardState;
  const displayBiasedWizard = globalConfig.displayBiasedWizard;

  const travelersColSpan: UitkLayoutConditionalGridSpan = { small: 2, medium: 12 };
  const preferredClassColSpan: UitkLayoutConditionalGridSpan = { small: 2, medium: 12 };
  const isPackageContainingFlight = !packageWizardState.isPackageHotelCar();
  const isPackageContainingCar = !packageWizardState.isPackageFlightHotel();

  const flightTypeSelectorInPillExperimentName = "newFlightsinPill";

  return (
    <>
      <UitkLayoutFlex space="six" className="packageSelectors" wrap="wrap">
        {displayNewPackageFlightType && (
          <Experiment name={flightTypeSelectorInPillExperimentName}>
            <ExperimentControl>
              <UitkLayoutFlexItem className="packageSelector">
                <FlightTypeMenu
                  onFlightTypeUpdate={onFlightTypeUpdate}
                  activeFlightType={activeFlightType}
                  showOneway={showOneway}
                  showMulticity={showMulticity}
                />
              </UitkLayoutFlexItem>
            </ExperimentControl>
            <ExperimentVariant bucket={1} />
          </Experiment>
        )}

        {currentSubLOB === PackagesSubLobs.FLIGHT_HOTEL && (
          <UitkLayoutFlexItem className="packageSelector">
            <Travelers
              tripLobType={routeStore.queryParams.pwaSubLOB}
              lobState={packageWizardState}
              colSpan={travelersColSpan}
              noRehydratationValues={noRehydratationValues}
              triggerType={TravelersTriggerTypes.LINK_BUTTON}
            />
          </UitkLayoutFlexItem>
        )}

        {currentSubLOB === PackagesSubLobs.FLIGHT_CAR && (
          <UitkLayoutFlexItem className="packageSelector">
            <Travelers
              tripLobType={routeStore.queryParams.pwaSubLOB}
              lobState={packageWizardState}
              colSpan={travelersColSpan}
              noRehydratationValues={{
                numOfTravelers: 1,
                name: "adultsPerRoom[1]",
              }}
              config={packageConfig.subLOB.flightCar.travelers}
              triggerType={TravelersTriggerTypes.LINK_BUTTON}
            />
          </UitkLayoutFlexItem>
        )}

        {currentSubLOB === PackagesSubLobs.FLIGHT_HOTEL_CAR && (
          <UitkLayoutFlexItem className="packageSelector">
            <Travelers
              tripLobType={routeStore.queryParams.pwaSubLOB}
              lobState={packageWizardState}
              colSpan={travelersColSpan}
              noRehydratationValues={noRehydratationValues}
              triggerType={TravelersTriggerTypes.LINK_BUTTON}
            />
          </UitkLayoutFlexItem>
        )}

        {currentSubLOB === PackagesSubLobs.HOTEL_CAR && (
          <UitkLayoutFlexItem className="packageSelector">
            <Travelers
              tripLobType={routeStore.queryParams.pwaSubLOB}
              lobState={packageWizardState}
              config={packageConfig.subLOB.hotelCar.travelers}
              colSpan={travelersColSpan}
              noRehydratationValues={noRehydratationValues}
              triggerType={TravelersTriggerTypes.LINK_BUTTON}
            />
          </UitkLayoutFlexItem>
        )}

        {packageConfig.showAllSubLOB && isPackageContainingFlight && (
          <>
            <UitkLayoutFlexItem className="packageSelector">
              <PreferredClassInput
                colSpan={preferredClassColSpan}
                onSelectPreferredClassCode={onSelectPreferredClassCode}
                wizardState={wizardState}
              />
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem className="packageSelector">
              <PreferredAirlineInput onSelectPreferredAirline={onSelectPreferredAirline} wizardState={wizardState} />
            </UitkLayoutFlexItem>
          </>
        )}

        {displayBiasedWizard && isPackageContainingCar && !carConfig.isBiasedWizardEmpty && !carConfig.carType && (
          <CarTypeDropdown carWizardState={carWizardState} />
        )}
      </UitkLayoutFlex>
    </>
  );
};
